import type { Annotation, AnnotationType } from "@/lib/api";

/** Drawn shapes — rectangle, ellipse, diamond, line, arrow, parallelogram,
 * terminator, hexagon, cylinder, table.
 *
 * THIS MODULE IS THE GEOMETRY CONTRACT. Three renderers draw the same
 * annotation — the Fabric editor (annotate mode), the SVG overlay (read mode),
 * and the server's reportlab flatten (`services/annotated_pdf.py`) — and if any
 * of them derives its own geometry, markup visibly jumps when you enter
 * annotate mode or silently prints wrong. The two client renderers import
 * everything here; the Python one mirrors it and says so in its docstring.
 *
 * A box shape (rect / ellipse / diamond) stores its bounding box as `rect` in
 * page-relative [0, 1]. A segment shape (line / arrow) stores its two endpoints
 * as `points`, NOT a box, so a segment drawn in any of the four diagonal
 * directions round-trips without a renderer having to guess which corner it
 * started from. Both are outline-only and share the pen's `color` +
 * `stroke_rel`, so a 3 pt rectangle weighs the same as a 3 pt stroke.
 */

export const BOX_SHAPES = [
  "rect",
  "ellipse",
  "diamond",
  // The rest of a flowchart's vocabulary. Every one is a figure inscribed in
  // the same bounding box, so they cost nothing beyond their outline: the
  // editor, the overlay and both PDF renderers all go through `boxShapePath`.
  "parallelogram",
  "terminator",
  "hexagon",
  "cylinder",
] as const;
export const SEGMENT_SHAPES = ["line", "arrow"] as const;

export type BoxShape = (typeof BOX_SHAPES)[number];
export type SegmentShape = (typeof SEGMENT_SHAPES)[number];
export type ShapeKind = BoxShape | SegmentShape;

const BOX = new Set<string>(BOX_SHAPES);
const SEGMENT = new Set<string>(SEGMENT_SHAPES);

export function isBoxShape(t: AnnotationType | string): t is BoxShape {
  return BOX.has(t);
}

export function isSegmentShape(t: AnnotationType | string): t is SegmentShape {
  return SEGMENT.has(t);
}

export function isShape(t: AnnotationType | string): t is ShapeKind {
  return BOX.has(t) || SEGMENT.has(t);
}

/** An absolute (already scaled to the page's pixel size) axis-aligned box. */
export interface AbsBox {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface Pt {
  x: number;
  y: number;
}

/** Normalize a drag from `start` to `end` into a top-left-origin box. Dragging
 * up and/or left is the same rectangle as dragging down and right — negative
 * width/height would round-trip as a box the renderers draw inside-out. */
export function boxFromDrag(start: Pt, end: Pt): AbsBox {
  return {
    x: Math.min(start.x, end.x),
    y: Math.min(start.y, end.y),
    w: Math.abs(end.x - start.x),
    h: Math.abs(end.y - start.y),
  };
}

/** The four corners of a diamond inscribed in `box` — its edge midpoints,
 * clockwise from the top. (Excalidraw's diamond is the same figure.) */
export function diamondPoints(box: AbsBox): Pt[] {
  const cx = box.x + box.w / 2;
  const cy = box.y + box.h / 2;
  return [
    { x: cx, y: box.y },
    { x: box.x + box.w, y: cy },
    { x: cx, y: box.y + box.h },
    { x: box.x, y: cy },
  ];
}

/** The ellipse INSCRIBED in `box` (centre + radii) — never circumscribed, so a
 * shape drawn inside a drag rectangle stays inside it. */
export function ellipseParams(box: AbsBox): { cx: number; cy: number; rx: number; ry: number } {
  return { cx: box.x + box.w / 2, cy: box.y + box.h / 2, rx: box.w / 2, ry: box.h / 2 };
}

/** How far an arrow head reaches back down the shaft. Derived from the stroke
 * width (so it scales with the pen) and capped at a fraction of the shaft (so a
 * short arrow keeps a head smaller than itself, never a lollipop).
 * KEEP IN SYNC with `_arrow_head` in server/.../services/annotated_pdf.py. */
export const ARROW_HEAD_SPREAD = (22 * Math.PI) / 180;

export function arrowHeadLength(segLength: number, strokeWidth: number): number {
  return Math.min(segLength * 0.3, strokeWidth * 6);
}

/** The two barb end points of an arrow head at `to`, pointing away from `from`.
 * Empty when the segment has no length (nothing to point along). */
export function arrowHead(from: Pt, to: Pt, strokeWidth: number): Pt[] {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const seg = Math.hypot(dx, dy);
  if (!(seg > 0)) return [];
  const len = arrowHeadLength(seg, strokeWidth);
  const angle = Math.atan2(dy, dx);
  return [angle - ARROW_HEAD_SPREAD, angle + ARROW_HEAD_SPREAD].map((a) => ({
    x: to.x - len * Math.cos(a),
    y: to.y - len * Math.sin(a),
  }));
}

/** A box shape's outline as a closed polyline in absolute pixels — what the
 * eraser hit-tests against, so erasing catches the DRAWN LINE rather than the
 * whole (empty) interior. An ellipse is sampled; 48 segments is smooth enough
 * that the eraser never falls between two samples at any realistic radius. */
export function outlinePoints(kind: BoxShape, box: AbsBox): Pt[] {
  if (kind === "rect") {
    return [
      { x: box.x, y: box.y },
      { x: box.x + box.w, y: box.y },
      { x: box.x + box.w, y: box.y + box.h },
      { x: box.x, y: box.y + box.h },
      { x: box.x, y: box.y },
    ];
  }
  if (kind === "diamond") {
    const p = diamondPoints(box);
    return [...p, p[0]!];
  }
  // The other polygonal figures close the same way.
  const poly = polygonFor(kind, box);
  if (poly) return [...poly, poly[0]!];
  if (kind === "terminator") {
    // Sampled for the eraser only: a stadium's ends are arcs, and the hit-test
    // works on polylines. Dense enough that the eraser never falls between two
    // samples at any realistic radius.
    const r = Math.min(box.h / 2, box.w / 2);
    const out: Pt[] = [];
    const arc = (cx: number, cy: number, from: number, to: number) => {
      for (let i = 0; i <= 16; i++) {
        const t = from + ((to - from) * i) / 16;
        out.push({ x: cx + r * Math.cos(t), y: cy + r * Math.sin(t) });
      }
    };
    out.push({ x: box.x + r, y: box.y });
    out.push({ x: box.x + box.w - r, y: box.y });
    arc(box.x + box.w - r, box.y + box.h / 2, -Math.PI / 2, Math.PI / 2);
    out.push({ x: box.x + r, y: box.y + box.h });
    arc(box.x + r, box.y + box.h / 2, Math.PI / 2, (3 * Math.PI) / 2);
    return out;
  }
  if (kind === "cylinder") {
    // Its silhouette: the lid, the sides, the base. Good enough to erase by.
    const c = cylinderCap(box);
    const rx = box.w / 2;
    const cx = box.x + rx;
    const out: Pt[] = [];
    for (let i = 0; i <= 24; i++) {
      const t = Math.PI + (Math.PI * i) / 24;
      out.push({ x: cx + rx * Math.cos(t), y: box.y + c + c * Math.sin(t) });
    }
    for (let i = 0; i <= 24; i++) {
      const t = (Math.PI * i) / 24;
      out.push({ x: cx + rx * Math.cos(t), y: box.y + box.h - c + c * Math.sin(t) });
    }
    out.push(out[0]!);
    return out;
  }
  const { cx, cy, rx, ry } = ellipseParams(box);
  const out: Pt[] = [];
  const STEPS = 48;
  for (let i = 0; i <= STEPS; i++) {
    const t = (i / STEPS) * Math.PI * 2;
    out.push({ x: cx + rx * Math.cos(t), y: cy + ry * Math.sin(t) });
  }
  return out;
}

/** The absolute box of a stored box-shape annotation on a page `w × h` px. */
export function absBox(a: Annotation, w: number, h: number): AbsBox | null {
  const r = a.rect;
  if (!r) return null;
  return { x: r.x * w, y: r.y * h, w: r.w * w, h: r.h * h };
}

/** The absolute endpoints of a stored segment-shape annotation. */
export function absSegment(a: Annotation, w: number, h: number): [Pt, Pt] | null {
  const p = a.points;
  if (!p || p.length !== 2) return null;
  return [
    { x: p[0]!.x * w, y: p[0]!.y * h },
    { x: p[1]!.x * w, y: p[1]!.y * h },
  ];
}

/** A shape's stroke width in pixels on a page `w` px wide. Same reference as
 * ink (the page WIDTH), so a shape and a stroke of the same `stroke_rel` are
 * the same weight. */
export function shapeStrokeWidth(strokeRel: number | null | undefined, w: number): number {
  return Math.max(1, (strokeRel ?? 0.004) * w);
}

/** The slant a parallelogram leans by, as a fraction of its width — capped
 * against the height so a short wide box doesn't collapse into a sliver. */
export function parallelogramSlant(box: AbsBox): number {
  return Math.min(box.w * 0.25, box.h * 0.5);
}

/** How far a hexagon's points inset from its left and right edges. */
export function hexagonInset(box: AbsBox): number {
  return Math.min(box.w * 0.22, box.h * 0.5);
}

/** A cylinder's elliptical cap height (the "lid" you see in perspective). */
export function cylinderCap(box: AbsBox): number {
  return Math.min(box.h * 0.18, box.w * 0.35);
}

/** The corner polygons of a flowchart box shape, in draw order. Shared by the
 * SVG path below, the eraser's outline hit-test, and the PDF renderers. */
export function polygonFor(kind: BoxShape, box: AbsBox): Pt[] | null {
  const { x, y, w, h } = box;
  if (kind === "rect") return outlinePoints("rect", box).slice(0, 4);
  if (kind === "diamond") return diamondPoints(box);
  if (kind === "parallelogram") {
    const s = parallelogramSlant(box);
    return [
      { x: x + s, y },
      { x: x + w, y },
      { x: x + w - s, y: y + h },
      { x, y: y + h },
    ];
  }
  if (kind === "hexagon") {
    const i = hexagonInset(box);
    const my = y + h / 2;
    return [
      { x: x + i, y },
      { x: x + w - i, y },
      { x: x + w, y: my },
      { x: x + w - i, y: y + h },
      { x: x + i, y: y + h },
      { x, y: my },
    ];
  }
  return null; // ellipse / terminator / cylinder are curved
}

/** An SVG path `d` for a box shape's outline. Polygonal figures come from
 * {@link polygonFor}; the curved ones (ellipse, terminator, cylinder) are drawn
 * with arcs so they stay true at any zoom rather than sampled. */
export function boxShapePath(kind: BoxShape, box: AbsBox, cornerRadius = 0): string {
  const { x, y, w, h } = box;
  if (kind === "rect" && cornerRadius > 0) {
    // Rounded by a FRACTION of the short side, so a rounded box stays
    // proportionate at any size — a pixel radius looks right on one shape and
    // wrong on the next.
    const r = Math.min(Math.min(w, h) * cornerRadius, Math.min(w, h) / 2);
    if (r > 0.5) {
      return (
        `M ${x + r} ${y} L ${x + w - r} ${y} A ${r} ${r} 0 0 1 ${x + w} ${y + r} ` +
        `L ${x + w} ${y + h - r} A ${r} ${r} 0 0 1 ${x + w - r} ${y + h} ` +
        `L ${x + r} ${y + h} A ${r} ${r} 0 0 1 ${x} ${y + h - r} ` +
        `L ${x} ${y + r} A ${r} ${r} 0 0 1 ${x + r} ${y} Z`
      );
    }
  }
  if (kind === "ellipse") {
    const { cx, cy, rx, ry } = ellipseParams(box);
    if (!(rx > 0) || !(ry > 0)) return "";
    return (
      `M ${cx - rx} ${cy} ` +
      `A ${rx} ${ry} 0 1 0 ${cx + rx} ${cy} ` +
      `A ${rx} ${ry} 0 1 0 ${cx - rx} ${cy} Z`
    );
  }
  if (kind === "terminator") {
    // A stadium: straight top and bottom, semicircular ends. The radius is half
    // the height, so a tall narrow box becomes a capsule rather than breaking.
    const r = Math.min(h / 2, w / 2);
    if (!(r > 0)) return "";
    return (
      `M ${x + r} ${y} L ${x + w - r} ${y} ` +
      `A ${r} ${r} 0 0 1 ${x + w - r} ${y + h} ` +
      `L ${x + r} ${y + h} ` +
      `A ${r} ${r} 0 0 1 ${x + r} ${y} Z`
    );
  }
  if (kind === "cylinder") {
    // Stored data: an elliptical lid, straight sides, and a curved base — plus
    // the far side of the lid, which is what makes it read as 3-D.
    const c = cylinderCap(box);
    if (!(w > 0) || !(h > 0)) return "";
    const rx = w / 2;
    return (
      `M ${x} ${y + c} ` +
      `A ${rx} ${c} 0 0 1 ${x + w} ${y + c} ` +
      `L ${x + w} ${y + h - c} ` +
      `A ${rx} ${c} 0 0 1 ${x} ${y + h - c} Z ` +
      `M ${x} ${y + c} A ${rx} ${c} 0 0 0 ${x + w} ${y + c}`
    );
  }
  const pts = polygonFor(kind, box);
  if (!pts?.length) return "";
  return `M ${pts.map((p) => `${p.x} ${p.y}`).join(" L ")} Z`;
}

/** A paint-bucket fill's rings, scaled to a page `w × h` px. Ring 0 is the outer
 * boundary; the rest are holes, which is why every renderer fills EVEN-ODD. */
export function absRings(a: Annotation, w: number, h: number): Pt[][] | null {
  const rings = (a as { rings?: { x: number; y: number }[][] | null }).rings;
  if (!rings?.length) return null;
  return rings.map((ring) => ring.map((p) => ({ x: p.x * w, y: p.y * h })));
}

/** One SVG path `d` for a set of rings — each ring a closed subpath, so a single
 * even-odd fill paints the area and leaves the holes. */
export function ringsPath(rings: Pt[][]): string {
  return rings
    .filter((r) => r.length >= 3)
    .map((r) => `M ${r.map((p) => `${p.x} ${p.y}`).join(" L ")} Z`)
    .join(" ");
}


// --- tables -------------------------------------------------------------------

/** A table's cell size is derived from the PEN, not from a fixed constant: the
 * point of a ruled table on a handwriting surface is that your writing fits
 * between the lines. A heavier pen means bigger handwriting, so the borders and
 * the whitespace inside each cell scale with it together.
 *
 * `CELL_PER_STROKE` is how many stroke-widths of clear space a cell leaves for
 * writing; the border itself is the stroke. Tuned so a 2.5 pt pen (the default)
 * gives a cell you can comfortably write a word in. */
export const CELL_PER_STROKE = 30;

/** Floor on a cell, as a fraction of the PAGE width. A hairline pen must still
 * produce a grid you can write in, and "some number of pixels" means nothing
 * here: the scene is the page's own coordinate space (a US-Letter page is 1700
 * units wide), so a pixel floor is a different physical size on every surface.
 * That confusion is what first shipped an 18-column table from one drag. */
export const MIN_CELL_FRACTION = 0.06;

/** A table cell's size, in the same units as `strokeWidthPx` and `pageWidthPx`
 * (both scene). At the default 2.5 pt pen this lands near a 1 inch cell — about
 * eight columns across a US-Letter page, which is a table you can actually write
 * in. That is the entire point of ruling a table on a handwriting surface, and
 * why the size comes from the pen rather than a constant. */
export function tableCellSize(strokeWidthPx: number, pageWidthPx: number): number {
  return Math.max(pageWidthPx * MIN_CELL_FRACTION, strokeWidthPx * CELL_PER_STROKE);
}

/** How many columns and rows a dragged box should hold, at this pen weight —
 * chosen so the cells come out close to {@link tableCellSize} rather than
 * squeezing a fixed grid into whatever was dragged. Always at least 1x1. */
export function tableTracksForBox(
  box: AbsBox,
  strokeWidthPx: number,
  pageWidthPx: number,
): { cols: number; rows: number } {
  const cell = tableCellSize(strokeWidthPx, pageWidthPx);
  return {
    cols: Math.max(1, Math.round(box.w / cell)),
    rows: Math.max(1, Math.round(box.h / cell)),
  };
}

/** Even track fractions for an n-track axis. */
export function evenTracks(n: number): number[] {
  const count = Math.max(1, Math.floor(n));
  return Array.from({ length: count }, () => 1 / count);
}

/** Track fractions normalized to sum to 1 (the renderers' contract — the server
 * deliberately does not require it, so a drag never has to round-trip through a
 * rejection). */
export function normalizeTracks(tracks: number[] | null | undefined, fallback = 1): number[] {
  const list = (tracks ?? []).filter((t) => t > 0);
  if (!list.length) return evenTracks(fallback);
  const total = list.reduce((a, b) => a + b, 0);
  return list.map((t) => t / total);
}

/** The absolute x offsets of a table's vertical lines (including both edges). */
export function trackOffsets(start: number, size: number, tracks: number[]): number[] {
  const out = [start];
  let at = start;
  for (const t of tracks) {
    at += t * size;
    out.push(at);
  }
  // Guard against float drift so the last line lands exactly on the edge.
  out[out.length - 1] = start + size;
  return out;
}

/** A table's grid as an SVG path: the outer box plus every interior line. */
export function tablePath(box: AbsBox, cols: number[], rows: number[]): string {
  const xs = trackOffsets(box.x, box.w, cols);
  const ys = trackOffsets(box.y, box.h, rows);
  const parts: string[] = [];
  for (const x of xs) parts.push(`M ${x} ${box.y} L ${x} ${box.y + box.h}`);
  for (const y of ys) parts.push(`M ${box.x} ${y} L ${box.x + box.w} ${y}`);
  return parts.join(" ");
}

/** Which grid line of a table a point is within `tolerance` of, if any. Drives
 * the Google-Docs-style drag: grab a boundary and move it. Edges are excluded —
 * dragging the outer frame is the object's own resize, not a track resize. */
export function tableLineAt(
  box: AbsBox,
  cols: number[],
  rows: number[],
  p: Pt,
  tolerance: number,
): { axis: "col" | "row"; index: number } | null {
  if (
    p.x < box.x - tolerance ||
    p.x > box.x + box.w + tolerance ||
    p.y < box.y - tolerance ||
    p.y > box.y + box.h + tolerance
  ) {
    return null;
  }
  const xs = trackOffsets(box.x, box.w, cols);
  for (let i = 1; i < xs.length - 1; i++) {
    if (Math.abs(p.x - xs[i]!) <= tolerance) return { axis: "col", index: i - 1 };
  }
  const ys = trackOffsets(box.y, box.h, rows);
  for (let i = 1; i < ys.length - 1; i++) {
    if (Math.abs(p.y - ys[i]!) <= tolerance) return { axis: "row", index: i - 1 };
  }
  return null;
}

/** Move the boundary after track `index` to `position` (absolute), taking the
 * difference out of the NEXT track — exactly how a spreadsheet or Google Docs
 * behaves: widening a column narrows its neighbour, and the table's own size
 * never changes. Both tracks keep a minimum so a column cannot be dragged out
 * of existence. */
export function resizeTrack(
  tracks: number[],
  index: number,
  start: number,
  size: number,
  position: number,
  minFraction = 0.02,
): number[] {
  if (index < 0 || index >= tracks.length - 1 || !(size > 0)) return tracks;
  const next = normalizeTracks(tracks);
  const before = next.slice(0, index).reduce((a, b) => a + b, 0);
  const pair = next[index]! + next[index + 1]!;
  const wanted = (position - start) / size - before;
  const first = Math.min(Math.max(wanted, minFraction), pair - minFraction);
  next[index] = first;
  next[index + 1] = pair - first;
  return next;
}

/** A shape's dash pattern, in the same units as its stroke width. Derived from
 * the stroke so a heavy dashed shape gets proportionately longer dashes rather
 * than a dotted mush. Shared by every renderer, or the same shape would dash
 * differently on screen and on paper. */
export function dashPattern(strokeWidth: number): [number, number] {
  return [strokeWidth * 3, strokeWidth * 2];
}
